<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed, ref, unref } from 'vue'
import { useMutation, useQuery } from '@vue/apollo-composable'
import {
  BopProcessViewDataQuery,
  BopProcessViewDataQueryVariables,
  BopProcessViewUpdateMutation,
  BopProcessViewUpdateMutationVariables,
  CreateModule,
  GraphEntityType,
  Mutation,
  PermissionOrganization,
  SetDagGraph,
  SetDagGraphNode,
  UpdateModule,
  VersionStatus,
} from '@/generated/graphql'
import gql from 'graphql-tag'
import Breadcrumb from '@/components/Breadcrumb.vue'
import { useRoute } from 'vue-router'
import { v4 } from 'uuid'
import NotificationSaved from '@/components/notifications/NotificationSaved.vue'
import ModuleUpsertDialog, { ModuleFields } from '@/components/module/ModuleUpsertDialog.vue'
import BopNavigationDrawer from '@/components/bop/BopNavigationDrawer.vue'
import DownloadBopModuleDialog from '@/components/mbom/DownloadBopModuleDialog.vue'
import { ComponentExposed } from 'vue-component-type-helpers'
import DeleteDialogNext from '@/components/dialogs/AsyncDeleteDialog.vue'
import AsyncDeleteDialog from '@/components/dialogs/AsyncDeleteDialog.vue'
import ModuleDag from '@/components/module/ModuleDag.vue'
import DuplicateModule from '@/components/module/DuplicateModule.vue'
import useAuthorization from '@/composables/useAuthorization'
import ArchivedConfigurationAlert from '@/components/archive/ArchivedConfigurationAlert.vue'
import { isBopLocked } from '@/utils/bopLock'

type LocalModuleNode = BopProcessViewDataQuery['product']['bop']['nodes'][0]

const { t } = useI18n()
const { hasOrgAuthz } = useAuthorization()
const route = useRoute()

const productId = computed(() => route.params.productId as string)
const productConfigId = computed(() => route.params.productConfigId as string)
const siteId = computed(() => route.params.siteId as string)
const bopId = computed(() => route.params.bopId as string)
const fetchQuery = useQuery<BopProcessViewDataQuery, BopProcessViewDataQueryVariables>(
  gql`
    query BopProcessViewData($siteId: ID!, $productId: ID!, $productConfigId: ID!, $bopId: ID!) {
      site {
        site(id: $siteId) {
          id
          abbreviation
        }
      }
      product {
        product(id: $productId) {
          id
          abbreviation
        }
        productConfiguration(id: $productConfigId) {
          id
          abbreviation
        }
        bop(id: $bopId) {
          id
          versionStatus
          productConfiguration {
            id
            archived
          }
          nodes {
            id
            parentIds
            module {
              id
              name
              abbreviation
              description
              inputs {
                id
                milliQuantity
                module {
                  id
                }
                output {
                  id
                  module {
                    id
                  }
                  article {
                    id
                    articleNumber
                    revision
                    unit
                  }
                }
              }
            }
          }
        }
      }
    }
  `,
  () => ({
    productId: productId.value,
    productConfigId: productConfigId.value,
    siteId: siteId.value,
    bopId: bopId.value,
  }),
)
const isWritable = computed(
  () =>
    !isBopLocked(fetchQuery.result.value?.product.bop) &&
    hasOrgAuthz(PermissionOrganization.OrgProductWrite),
)
const canMbom = computed(
  () =>
    fetchQuery.result.value?.product.bop.versionStatus == VersionStatus.Released &&
    hasOrgAuthz(PermissionOrganization.OrgMbomRead),
)
const fetchedNodes = computed<LocalModuleNode[]>(
  () => fetchQuery.result.value?.product.bop.nodes || [],
)

function nodeText(node: LocalModuleNode) {
  return node.module.name
}

const nodeModel = ref<SetDagGraphNode>()

const moduleDialog = ref<ComponentExposed<typeof ModuleUpsertDialog>>()
function draftModule(nodeId: string, parentIds: string[]) {
  const moduleId = v4()
  nodeModel.value = {
    id: nodeId,
    parentIds: parentIds,
    entityType: GraphEntityType.Module,
    entityId: moduleId,
  }
  moduleDialog.value?.open({ create: true, initial: { id: moduleId }, save: create })
}

const notifySaved = ref(false)
function moduleNodesToGraphNodes(nodes: LocalModuleNode[]): SetDagGraphNode[] {
  return nodes.map((n) => moduleNodeToGraphNode(n))
}
function moduleNodeToGraphNode(node: LocalModuleNode): SetDagGraphNode {
  return {
    id: node.id,
    parentIds: node.parentIds,
    entityType: GraphEntityType.Module,
    entityId: node.module.id,
  }
}

const setGraphMutation = useMutation<Mutation, { graph: SetDagGraph }>(gql`
  mutation setGraph($graph: SetDAGGraph!) {
    product {
      setBillOfProcessGraph(command: $graph)
    }
  }
`)
function setGraph(nodes: LocalModuleNode[]) {
  setGraphMutation
    .mutate({
      graph: {
        entityId: bopId.value,
        nodes: moduleNodesToGraphNodes(nodes),
      },
    })
    .then(() => {
      notifySaved.value = true
      fetchQuery.refetch()
    })
}

const createMutation = useMutation<Mutation, { graph: SetDagGraph; module: CreateModule }>(gql`
  mutation createModule($module: CreateModule!, $graph: SetDAGGraph!) {
    product {
      createModule(command: $module) {
        id
      }
      setBillOfProcessGraph(command: $graph)
    }
  }
`)

async function create(fields: ModuleFields) {
  const nodes = [...moduleNodesToGraphNodes(fetchedNodes.value), nodeModel.value as SetDagGraphNode]

  await createMutation.mutate({
    graph: {
      entityId: bopId.value,
      nodes,
    },
    module: { ...fields, productId: productId.value } as CreateModule,
  })
  notifySaved.value = true
  fetchQuery.refetch()
}

function selectForUpdate(node: LocalModuleNode) {
  nodeModel.value = moduleNodeToGraphNode(node)
  moduleDialog.value?.open({ initial: node.module, save: update })
}
const updateMutation = useMutation<
  BopProcessViewUpdateMutation,
  BopProcessViewUpdateMutationVariables
>(gql`
  mutation BopProcessViewUpdate($module: UpdateModule!) {
    product {
      updateModule(command: $module) {
        id
      }
    }
  }
`)

async function update(fields: ModuleFields) {
  await updateMutation.mutate({ module: fields as UpdateModule })
  notifySaved.value = true
  fetchQuery.refetch()
}

const duplicateModuleDialog = ref<ComponentExposed<typeof DuplicateModule>>()
const doDuplicate = async () => {
  duplicateModuleDialog.value?.open()
}

const deleteMutation = useMutation<Mutation, { id: string }>(gql`
  mutation deleteModule($id: ID!) {
    product {
      deleteModule(id: $id)
    }
  }
`)
const deleteDialog = ref<ComponentExposed<typeof DeleteDialogNext>>()
const doDelete = async (item: LocalModuleNode) => {
  await deleteDialog.value?.open(item.module.name, () =>
    deleteMutation.mutate({ id: item.module.id }).then(() => fetchQuery.refetch()),
  )
}

const downloadMbomOpen = ref(false)
const downloadWorkInstructionsOpen = ref(false)
</script>

<template>
  <breadcrumb />
  <archived-configuration-alert :product-configuration-id="productConfigId" />
  <bop-navigation-drawer :bop-id="bopId" />

  <h1>
    {{ t('entity.bop.singular') }}

    <span class="float-right text-right">
      <v-btn-group v-if="isWritable">
        <v-btn color="primary" icon="add" @click="draftModule(v4(), [])" />
        <v-btn variant="outlined" icon="content_copy" @click="doDuplicate" />
      </v-btn-group>

      <template v-if="canMbom">
        <v-btn color="primary" @click="downloadMbomOpen = true">
          {{ t('view.organization.bopProcess.downloadMbomButton') }}
        </v-btn>
        <download-bop-module-dialog v-model="downloadMbomOpen" :bop-id="bopId" type="mbom" />

        <v-btn class="ml-4" color="primary" @click="downloadWorkInstructionsOpen = true">
          {{ t('view.organization.bopProcess.downloadWorkInstructionsButton') }}
        </v-btn>
        <download-bop-module-dialog
          v-model="downloadWorkInstructionsOpen"
          :bop-id="bopId"
          type="work-instructions"
        />
      </template>
    </span>
  </h1>

  <module-dag
    :nodes="fetchedNodes as any"
    :is-editable="unref(isWritable)"
    :node-text="nodeText"
    :create-node="draftModule"
    @nodes="setGraph"
  >
    <template #menu-items-prepend="{ node }">
      <router-link :to="{ name: 'module', params: { moduleId: node.module.id } }">
        <v-list-item>
          <v-list-item-title>
            {{ t('view.organization.bopProcess.menuView') }}
          </v-list-item-title>
        </v-list-item>
      </router-link>

      <template v-if="isWritable">
        <v-list-item @click="selectForUpdate(node)">
          <v-list-item-title>
            {{ t('view.organization.bopProcess.menuUpdateNode') }}
          </v-list-item-title>
        </v-list-item>
        <v-list-item
          v-if="hasOrgAuthz(PermissionOrganization.OrgProductDelete)"
          @click="doDelete(node)"
        >
          <v-list-item-title>
            {{ t('view.organization.bopProcess.menuDeleteNode') }}
          </v-list-item-title>
        </v-list-item>
      </template>
    </template>
  </module-dag>

  <duplicate-module
    ref="duplicateModuleDialog"
    :product-id="productId"
    :target-bop-id="bopId"
    :initial-site-id="siteId"
    :initial-config-id="productConfigId"
    @duplicated="fetchQuery.refetch()"
  />

  <module-upsert-dialog ref="moduleDialog" :bop-id="bopId" />

  <async-delete-dialog ref="deleteDialog" :warning="t('entity.module.deleteWarning')" hindrance />

  <notification-saved v-model="notifySaved" />
</template>

<style lang="scss"></style>
